import moment from "moment";
import { esc, printHtml, tableHtml } from "../../utils/printDoc";
import { parseFolliDate, readFolliculogramVersions } from "./Folliculogram";

// Printouts and summaries for one treatment cycle, in the layout of the
// clinical Letters & Templates "medication schedule": the cycle, the drugs by
// day, and the appointments that fall in it.

const SIZE_LABELS = [">25", ...Array.from({ length: 16 }, (_, i) => String(25 - i)), "6-9", "2-5", "Total"];

export function latestDays(json) {
  const versions = readFolliculogramVersions(json);
  const v = versions[versions.length - 1];
  return v ? { days: v.days, number: v.number, count: versions.length } : { days: [], number: null, count: 0 };
}

const dateOf = (d) => parseFolliDate(d.date);

export function cycleSpan(days) {
  const dates = days.map(dateOf).filter(Boolean).sort((a, b) => a - b);
  if (!dates.length) return null;
  const from = moment(dates[0]);
  const to = moment(dates[dates.length - 1]);
  return { from, to, count: to.diff(from, "days") + 1 };
}

// Follicles seen in total, right and left, across the scan days.
export function follicleTotals(days) {
  let right = 0;
  let left = 0;
  days.forEach((d) => {
    const total = (d.Values || [])[SIZE_LABELS.length - 1] || {};
    right += parseInt(total.Col_R, 10) || 0;
    left += parseInt(total.Col_l ?? total.Col_L, 10) || 0;
  });
  return { right, left };
}

const dayLabel = (d) => {
  const m = dateOf(d);
  return m ? `${moment(m).format("ddd")}<br>${moment(m).format("DD-MMM")}` : esc(d.date);
};

// Drugs by name across the days, as the folliculogram records them.
function drugRows(days) {
  const names = [];
  days.forEach((d) => (d.DrugsValue || []).forEach((x) => {
    if (x?.DrugName && !names.includes(x.DrugName)) names.push(x.DrugName);
  }));
  return names.map((name) => [
    name,
    ...days.map((d) => {
      const hit = (d.DrugsValue || []).find((x) => x?.DrugName === name);
      return hit?.DrugsVAlues ?? hit?.DrugsValues ?? "";
    }),
  ]);
}

const chunk = (list, n) => Array.from({ length: Math.ceil(list.length / n) }, (_, i) => list.slice(i * n, (i + 1) * n));

function dayTable(days, rows, label) {
  return chunk(days, 10)
    .map((part, i) => {
      const offset = i * 10;
      const headers = [label, ...part.map((d) => ({ html: dayLabel(d) }))];
      const head = `<th>${esc(label)}</th>${part.map((d) => `<th>${dayLabel(d)}</th>`).join("")}`;
      const body = rows
        .map((r) => `<tr><td><strong>${esc(r[0])}</strong></td>${part.map((_, j) => `<td>${esc(r[1 + offset + j] ?? "")}</td>`).join("")}</tr>`)
        .join("");
      return headers && `<table class="wide"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
    })
    .join("");
}

export function appointmentsRows(appointments) {
  return appointments.map((a) => [
    moment(a.startTime).format("ddd DD/MM/YYYY HH:mm"),
    a.duration ? `${a.duration} min` : "",
    a.subject || a.appointmentlibraryitemName || "Appointment",
    a.roomName || "",
    a.bookingStatusName || "",
  ]);
}

// The cycle's appointments: those inside the folliculogram's dates (a few days
// either side), or all the upcoming ones when it has no dates yet.
export function appointmentsIn(appointments, span) {
  const list = [...appointments].sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
  if (!span) return list.filter((a) => moment(a.startTime).isSameOrAfter(moment().startOf("day")));
  const from = span.from.clone().subtract(3, "days").startOf("day");
  const to = span.to.clone().add(3, "days").endOf("day");
  return list.filter((a) => moment(a.startTime).isBetween(from, to, undefined, "[]"));
}

// "Medication schedule": cycle header, drugs by day, appointments.
export function printSchedule({ title, couple, type, start, cycle, appointments }) {
  const { days } = latestDays(cycle?.folliculagramJSON);
  const span = cycleSpan(days);
  const drugs = drugRows(days);
  const appts = appointmentsIn(appointments || [], span);

  const body = `
    <div class="kv">
      <div><span>Treatment</span>${esc(title)}</div>
      <div><span>Couple</span>${esc(couple || "")}</div>
      <div><span>Type</span>${esc(type || "")}</div>
      <div><span>Dates</span>${span ? `${span.from.format("DD-MMM-YYYY")} – ${span.to.format("DD-MMM-YYYY")} (${span.count} days)` : esc(start || "")}</div>
    </div>
    <h2>Drug schedule</h2>
    ${drugs.length ? dayTable(days, drugs, "Drug") : '<p class="muted">No drugs have been scheduled for this treatment yet.</p>'}
    <h2>Appointments</h2>
    ${appts.length ? tableHtml(["When", "Length", "What", "Where", "Status"], appointmentsRows(appts)) : '<p class="muted">No appointments fall in this treatment.</p>'}
    <p class="muted">Doses are as planned by your clinic and may change. Follow the instructions you are given.</p>`;
  return printHtml({ title: "Medication schedule", subtitle: title, bodyHtml: body });
}

// The folliculogram itself: follicles by size, hormones and drugs by scan day.
export function printFolliculogram({ title, couple, type, cycle }) {
  const { days, number, count } = latestDays(cycle?.folliculagramJSON);
  const span = cycleSpan(days);
  const sizes = SIZE_LABELS.map((label, i) => [
    `${label} mm`.replace("Total mm", "Total"),
    ...days.map((d) => {
      const v = (d.Values || [])[i] || {};
      const r = v.Col_R ?? "";
      const l = v.Col_l ?? v.Col_L ?? "";
      return r === "" && l === "" ? "" : `${r || "–"} / ${l || "–"}`;
    }),
  ]).filter((r, i) => i === SIZE_LABELS.length - 1 || r.slice(1).some(Boolean));
  const hormones = [["Endometrium", "Endome"], ["E2", "E2"], ["LH", "LH"], ["P4", "p4"], ["FSH", "FSH"], ["Prolactin", "PROL"]]
    .map(([label, key]) => [label, ...days.map((d) => d[key] ?? "")])
    .filter((r) => r.slice(1).some((v) => String(v).trim() !== ""));
  const drugs = drugRows(days);
  const totals = follicleTotals(days);

  const body = `
    <div class="kv">
      <div><span>Treatment</span>${esc(title)}</div>
      <div><span>Couple</span>${esc(couple || "")}</div>
      <div><span>Type</span>${esc(type || "")}</div>
      <div><span>Dates</span>${span ? `${span.from.format("DD-MMM-YYYY")} – ${span.to.format("DD-MMM-YYYY")}` : ""}</div>
      <div><span>Version</span>${number ?? ""}${count > 1 ? ` of ${count}` : ""}</div>
      <div><span>Right follicles</span>${totals.right}</div>
      <div><span>Left follicles</span>${totals.left}</div>
    </div>
    <h2>Follicles by size (right / left)</h2>
    ${days.length ? dayTable(days, sizes, "Size") : '<p class="muted">No folliculogram has been recorded yet.</p>'}
    ${hormones.length ? `<h2>Hormones</h2>${dayTable(days, hormones, "Hormone")}` : ""}
    ${drugs.length ? `<h2>Drugs</h2>${dayTable(days, drugs, "Drug")}` : ""}`;
  return printHtml({ title: "Folliculogram", subtitle: title, bodyHtml: body });
}

// Dates the cycle record carries, as "key dates" chips, with the day of the
// cycle each falls on (D1 is the first folliculogram day).
const KEY_DATES = [
  ["First injection", /^firstinjectiondate$/i],
  ["Trigger", /trigger.*(date|time)|(date|time).*trigger/i],
  ["Egg collection", /(egg|oocyte).*(collect|retriev).*date|date.*(egg|oocyte).*(collect|retriev)/i],
  ["Fertilisation check", /fertili[sz]ation.*check/i],
  ["Embryo transfer", /transfer.*date|date.*transfer/i],
  ["Pregnancy test", /early_outcome|pregnancy.*test.*date|date.*pregnancy.*test/i],
];

export function keyDatesOf(cycle, firstDay) {
  const out = [];
  if (!cycle) return out;
  const d1 = firstDay ? moment(firstDay).startOf("day") : null;
  KEY_DATES.forEach(([label, re]) => {
    const key = Object.keys(cycle).find((k) => re.test(k) && cycle[k] && !String(cycle[k]).startsWith("0001-01-01") && moment(cycle[k]).isValid());
    if (!key) return;
    const m = moment(cycle[key]);
    out.push({ label, date: m, day: d1 ? m.clone().startOf("day").diff(d1, "days") + 1 : null });
  });
  return out.sort((a, b) => a.date - b.date);
}
